import React, { useContext, useEffect, useState } from 'react';
import {
    Badge,
    Box,
    Heading,
    HStack,
    Icon,
    Spinner,
    Tag,
    TagLabel,
    Text,
    VStack,
} from '@chakra-ui/react';
import { FiActivity } from 'react-icons/fi';
import axios from 'axios';
import AuthContext from '../context/AuthContext';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

const SEVERITY_COLOR = {
    critical: 'red',
    high: 'orange',
    medium: 'yellow',
    low: 'gray',
};

const formatEvidenceValue = (value) => {
    if (value === null || value === undefined || value === '') {
        return '–';
    }
    if (typeof value === 'number') {
        return Number.isInteger(value) ? value.toLocaleString() : value.toFixed(2);
    }
    return String(value);
};

const ProductDataSignalsPanel = ({
    roleFilter,
    limit = 25,
    title = 'Live product-data signals',
    emptyMessage = 'No active product, evidence, drift, or risk-mismatch signals.',
    helperText,
}) => {
    const { token, user } = useContext(AuthContext);
    const [signals, setSignals] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const subscription = user?.subscription;
    const role = user?.role;

    useEffect(() => {
        const eligible = subscription === 'business' &&
            ['admin', 'head', 'compliance', 'portfolio_adviser'].includes(role);
        if (!token || !eligible) {
            setLoading(false);
            return;
        }

        let cancelled = false;
        const fetchSignals = async () => {
            setLoading(true);
            try {
                const response = await axios.get(
                    `${API_BASE_URL}/content/product-data/signals?limit=${limit}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (!cancelled) {
                    setSignals(response.data || null);
                    setError(null);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(
                        err.response?.data?.error?.detail ||
                        err.response?.data?.message ||
                        err.message
                    );
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchSignals();
        return () => { cancelled = true; };
    }, [token, subscription, role, limit]);

    if (subscription !== 'business' || !['admin', 'head', 'compliance', 'portfolio_adviser'].includes(role)) {
        return null;
    }

    const filtered = (signals?.signals || []).filter((signal) => {
        if (!roleFilter) {
            return true;
        }
        const normalized = (signal.role || '').toLowerCase();
        return Array.isArray(roleFilter)
            ? roleFilter.some((r) => normalized.includes(r.toLowerCase()))
            : normalized.includes(roleFilter.toLowerCase());
    });

    return (
        <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl" p={5}>
            <HStack justify="space-between" mb={3} flexWrap="wrap" gap={2}>
                <HStack spacing={2}>
                    <Icon as={FiActivity} color="brand.navy" />
                    <Heading size="sm" color="gray.800">{title}</Heading>
                    <Badge colorScheme="green" borderRadius="full">computed</Badge>
                </HStack>
                {signals?.total_signals != null && (
                    <Text fontSize="xs" color="gray.500">
                        {signals.total_signals} total · {filtered.length} for this role
                    </Text>
                )}
            </HStack>
            {helperText && (
                <Text fontSize="xs" color="gray.500" mb={3}>{helperText}</Text>
            )}
            {loading ? (
                <HStack>
                    <Spinner size="sm" color="brand.teal" />
                    <Text fontSize="sm" color="gray.600">Loading signals…</Text>
                </HStack>
            ) : error ? (
                <Text fontSize="sm" color="red.600">{error}</Text>
            ) : filtered.length === 0 ? (
                <Text fontSize="sm" color="gray.500">{emptyMessage}</Text>
            ) : (
                <VStack align="stretch" spacing={2}>
                    {filtered.map((signal) => (
                        <Box
                            key={signal.signal_id}
                            border="1px solid"
                            borderColor="gray.100"
                            borderRadius="lg"
                            p={3}
                            bg="gray.50"
                        >
                            <HStack justify="space-between" mb={1} flexWrap="wrap" gap={2}>
                                <HStack spacing={2}>
                                    <Badge colorScheme={SEVERITY_COLOR[signal.severity] || 'gray'}>
                                        {signal.severity}
                                    </Badge>
                                    <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                        {signal.title}
                                    </Text>
                                </HStack>
                                <Text fontSize="xs" color="gray.500">{signal.role}</Text>
                            </HStack>
                            <Text fontSize="xs" color="gray.600" mb={2}>{signal.description}</Text>
                            {signal.recommended_action && (
                                <Text fontSize="xs" color="brand.navy" mb={2}>
                                    <b>Recommended:</b> {signal.recommended_action}
                                </Text>
                            )}
                            {signal.evidence && Object.keys(signal.evidence).length > 0 && (
                                <HStack spacing={2} flexWrap="wrap">
                                    {Object.entries(signal.evidence).map(([key, value]) => (
                                        <Tag key={key} size="sm" variant="subtle" colorScheme="gray">
                                            <TagLabel>
                                                {key}: {formatEvidenceValue(value)}
                                            </TagLabel>
                                        </Tag>
                                    ))}
                                </HStack>
                            )}
                        </Box>
                    ))}
                </VStack>
            )}
        </Box>
    );
};

export default ProductDataSignalsPanel;
